const DB_NAME = 'casa-alessio-pos-offline';
const DB_VERSION = 1;
const CACHE_STORE = 'cache';
const SALES_STORE = 'ventas_offline';
const META_STORE = 'meta';

let dbPromise = null;

function abrirDb() {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('IndexedDB no está disponible.'));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error || new Error('No se pudo abrir el almacenamiento offline.'));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(SALES_STORE)) {
        const store = db.createObjectStore(SALES_STORE, { keyPath: 'idempotency_key' });
        store.createIndex('contexto', 'contexto_key', { unique: false });
        store.createIndex('estado', 'estado', { unique: false });
        store.createIndex('creada_en', 'creada_en', { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
  return dbPromise;
}

function completar(tx, request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || tx.error || new Error('Error de almacenamiento offline.'));
  });
}

export function contextoKey(usuario) {
  if (!usuario) return 'sin-contexto';
  return [usuario.id, usuario.empresa_id, usuario.sucursal_id, usuario.deposito_id].map((v) => Number(v) || 0).join(':');
}

export function normalizarPathCache(path) {
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.delete('t');
    const params = [...url.searchParams.entries()].sort(([a], [b]) => a.localeCompare(b));
    url.search = '';
    for (const [k, v] of params) url.searchParams.append(k, v);
    return `${url.pathname}${url.search}`;
  } catch {
    return String(path || '').replace(/([?&])t=\d+(&|$)/, '$1').replace(/[?&]$/, '');
  }
}

export function esRutaCacheable(path) {
  const p = normalizarPathCache(path);
  return [
    '/api/productos',
    '/api/clientes',
    '/api/categorias',
    '/api/configuracion',
    '/api/caja/actual',
    '/api/promociones/activas',
    '/api/contextos',
    '/api/dashboard',
    '/api/offline/bootstrap',
  ].some((base) => p === base || p.startsWith(`${base}?`));
}

function cacheKey(path, usuario) {
  return `${contextoKey(usuario)}|${normalizarPathCache(path)}`;
}

export async function guardarCache(path, data, usuario, metadata = {}) {
  if (!esRutaCacheable(path)) return;
  const db = await abrirDb();
  const tx = db.transaction(CACHE_STORE, 'readwrite');
  await completar(tx, tx.objectStore(CACHE_STORE).put({
    key: cacheKey(path, usuario),
    path: normalizarPathCache(path),
    contexto_key: contextoKey(usuario),
    data,
    metadata,
    actualizado_en: new Date().toISOString(),
  }));
}

export async function leerCache(path, usuario) {
  if (!esRutaCacheable(path)) return null;
  const db = await abrirDb();
  const tx = db.transaction(CACHE_STORE, 'readonly');
  return completar(tx, tx.objectStore(CACHE_STORE).get(cacheKey(path, usuario)));
}

export async function guardarBootstrap(bootstrap, usuario) {
  if (!bootstrap) return;
  const metadata = { catalogo_fecha: bootstrap.catalogo_fecha, generado_en: bootstrap.generado_en };
  await Promise.all([
    guardarCache('/api/offline/bootstrap', bootstrap, usuario, metadata),
    guardarCache('/api/productos', bootstrap.productos || [], usuario, metadata),
    guardarCache('/api/clientes', bootstrap.clientes || [], usuario, metadata),
    guardarCache('/api/categorias', bootstrap.categorias || [], usuario, metadata),
    guardarCache('/api/configuracion', bootstrap.configuracion || {}, usuario, metadata),
    guardarCache('/api/caja/actual', bootstrap.caja || {}, usuario, metadata),
    guardarCache('/api/promociones/activas', bootstrap.promociones || [], usuario, metadata),
  ]);
}

export async function obtenerMeta(key) {
  const db = await abrirDb();
  const tx = db.transaction(META_STORE, 'readonly');
  const row = await completar(tx, tx.objectStore(META_STORE).get(key));
  return row?.value;
}

export async function guardarMeta(key, value) {
  const db = await abrirDb();
  const tx = db.transaction(META_STORE, 'readwrite');
  await completar(tx, tx.objectStore(META_STORE).put({ key, value, actualizado_en: new Date().toISOString() }));
  return value;
}

export async function obtenerDispositivoId() {
  let id = await obtenerMeta('dispositivo_id');
  if (id) return id;
  id = (window.crypto?.randomUUID?.() || `pwa-${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9._:-]/g, '');
  await guardarMeta('dispositivo_id', id);
  return id;
}

export async function siguienteSecuencia(usuario) {
  const key = `secuencia:${contextoKey(usuario)}`;
  const actual = Number(await obtenerMeta(key) || 0) + 1;
  await guardarMeta(key, actual);
  return actual;
}

export async function encolarVenta(venta) {
  const db = await abrirDb();
  const tx = db.transaction(SALES_STORE, 'readwrite');
  await completar(tx, tx.objectStore(SALES_STORE).put(venta));
  window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  return venta;
}

export async function actualizarVentaOffline(idempotencyKey, cambios) {
  const db = await abrirDb();
  const txLectura = db.transaction(SALES_STORE, 'readonly');
  const actual = await completar(txLectura, txLectura.objectStore(SALES_STORE).get(idempotencyKey));
  if (!actual) return null;
  const nueva = { ...actual, ...cambios, actualizado_en: new Date().toISOString() };
  const txEscritura = db.transaction(SALES_STORE, 'readwrite');
  await completar(txEscritura, txEscritura.objectStore(SALES_STORE).put(nueva));
  window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  return nueva;
}

export async function eliminarVentaOffline(idempotencyKey) {
  const db = await abrirDb();
  const tx = db.transaction(SALES_STORE, 'readwrite');
  await completar(tx, tx.objectStore(SALES_STORE).delete(idempotencyKey));
  window.dispatchEvent(new CustomEvent('offline-queue-changed'));
}

export async function listarVentasOffline({ usuario = null, incluirSincronizadas = true } = {}) {
  const db = await abrirDb();
  const tx = db.transaction(SALES_STORE, 'readonly');
  const todas = await completar(tx, tx.objectStore(SALES_STORE).getAll());
  const contexto = usuario ? contextoKey(usuario) : null;
  return (todas || [])
    .filter((v) => !contexto || v.contexto_key === contexto)
    .filter((v) => incluirSincronizadas || v.estado !== 'sincronizada')
    .sort((a, b) => String(a.creada_en).localeCompare(String(b.creada_en)));
}

export async function resumenVentasOffline(usuario = null) {
  const ventas = await listarVentasOffline({ usuario, incluirSincronizadas: true });
  return ventas.reduce((acc, v) => {
    acc.total += 1;
    if (v.estado === 'pendiente' || v.estado === 'sincronizando') acc.pendientes += 1;
    if (v.estado === 'conflicto') acc.conflictos += 1;
    if (v.estado === 'sincronizada') acc.sincronizadas += 1;
    return acc;
  }, { total: 0, pendientes: 0, conflictos: 0, sincronizadas: 0 });
}

export async function descontarStockCache(usuario, productosVenta) {
  const row = await leerCache('/api/productos', usuario);
  if (!row || !Array.isArray(row.data)) return;
  const cantidades = new Map();
  for (const item of productosVenta || []) {
    if (item.es_manual) continue;
    const id = Number(item.producto_id || item.id);
    const cantidad = Number(item.cantidad || 0);
    if (id > 0 && cantidad > 0) cantidades.set(id, (cantidades.get(id) || 0) + cantidad);
  }
  const productos = row.data.map((p) => cantidades.has(Number(p.id))
    ? { ...p, cantidad_stock: Math.round((Number(p.cantidad_stock || 0) - cantidades.get(Number(p.id))) * 1000) / 1000 }
    : p);
  await guardarCache('/api/productos', productos, usuario, row.metadata || {});
}
